import {
  Component,
  OnInit,
  Input,
  ViewChild,
  Output,
  EventEmitter,
  ElementRef,
  Inject,
  Optional,
  ChangeDetectorRef,
  SimpleChanges,
  OnDestroy
} from '@angular/core';
import { Icons } from '@vex/icons/icons';
import { AiService } from '@vex/http-service/ai.service';
import { AiMessage, AiSetting, AskByInventoryIdViewModel } from '@vex/model/ai';
import { FormControl, FormGroup } from '@angular/forms';
import { AuthService } from '@vex/http-service/auth.service';
import { LayoutService } from '@vex/services/layout.service';
import { ToastrService } from 'ngx-toastr';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { KumobotSettings } from './kumobot-setting/kumobot-setting.component';
import { SignalRService } from '@vex/services/signal-r.service';
import { KumobotService } from '@vex/services/kumobot/kumobot.service';
import { Router } from '@angular/router';
import { PreDeploymentDialogueComponent } from '../pre-deployment-dialogue/pre-deployment-dialogue.component';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

declare var uuid: any;
@Component({
  selector: 'elastic-kumobot',
  templateUrl: './kumobot.component.html',
  styleUrls: ['./kumobot.component.scss']
})
export class KumobotComponent extends Icons implements OnInit, OnDestroy{
  formChat = new FormGroup({
    message: new FormControl('')
  });

  toggleMode: string = '';
  isCopied: boolean = false;
  messages: any[] = [];
  fetchingAiResponse = false;

  get agentSubtitle(): string {
    if (this.isGlobal) return 'Global Search';
    const map: Record<string, string> = {
      inventory:  'Inventory Assistant',
      device:     'Device Inspector',
      blueprint:  this.toggleMode === 'Prompt-to-Blueprint' ? 'Blueprint Designer' : 'Blueprint Co-Pilot',
      'ip-pool':  'IP Pool Manager',
      deployment: 'Conflict Resolver',
      job:        'Job Assistant'
    };
    return map[this.type] ?? 'Assistant';
  }
  @Input() inventoryId = '';
  @Input() deviceId = '';
  @Input() isReview = false;
  @Input() type = 'inventory';
  @Input() typeData: any = {};
  @Input() isGlobal = false;
  @Input() hideFullScreenIcon = false;
  @Input() isInventoryChild = false;
  @Output() nextStepper = new EventEmitter<void>();
  @Input() needMoreHeight: boolean = false;
  @Input() isOpenOnModal: boolean = false;
  @Input() isDetail: boolean = false;

  chatHistory: AskByInventoryIdViewModel = <AskByInventoryIdViewModel>{};
  uploadedFileContent: string = '';
  uploadedFileName: string = '';
  fileError: string = '';
  showFileUploadDetails: string = '';
  isFileUpload: boolean = false;
  matchedElements: HTMLElement[] = [];
  currentMatchIndex: number = 0;
  @ViewChild('markdownContent', { static: false }) markdownContent;
  @ViewChild('markdownContent2', { static: false }) markdownContent2;
  searchControl: FormControl = new FormControl('');

  features: { title: string; description: string }[] = [];
  aiSettingFromDialog: AiSetting | null = null;
  @Output() fullScreenToggle = new EventEmitter<boolean>();
  @Output() emitMessage = new EventEmitter<any>();
  @Input() fullScreen: boolean = false;
  @Input() messageInput: any[] = [];
  currentId: string = '';
  firstMessage: string = '';
  @Input() postingBlueprint: any[] = [];
  @Input() conflictContext: any = null;
  serviceConflictCopilotResponse: any;
  serviceConflictCopilotLoading: boolean = false;
  hideFSIcon: boolean = false;

  // Tracks tool-call steps for the currently streaming agent response.
  // Populated by ToolCallStarted / ToolCallCompleted SignalR events from the backend.
  private activeSteps: any[] = [];

  // Rotating thinking labels — cycles while fetchingAiResponse is true.
  thinkingLabel = 'Thinking';
  private thinkingLabels = [
    'Thinking', 'Reasoning', 'Analysing', 'Processing',
    'Searching', 'Synthesising', 'Planning', 'Inferring',
    'Connecting the dots', 'Inspecting', 'Evaluating', 'Exploring'
  ];
  private thinkingLabelIndex = 0;
  private thinkingInterval: any;
  private destroy$ = new Subject<void>();

  constructor(
    private ai: AiService,
    private authService: AuthService,
    public layoutService: LayoutService,
    private toastrService: ToastrService,
    private dialog: MatDialog,
    private signal: SignalRService,
    private kumobotService: KumobotService,
    private elRef: ElementRef,
    private router: Router,
    private changeDetectorRef: ChangeDetectorRef,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    super();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['messageInput'] && changes['messageInput'].currentValue) {
      if (!this.messages || this.messages.length === 0 || changes['messageInput'].isFirstChange()) {
        this.messages = changes['messageInput'].currentValue;
      }
    } 
    setTimeout(() => this.scrollToBottom(), 0);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    clearInterval(this.thinkingInterval);
    this.thinkingInterval = undefined;
  }

  tableHeight: string = '';
  ngOnInit(): void {
    let height = 360;
    if(this.isOpenOnModal){
      height = 291;
    } else if(this.needMoreHeight){
      height = 223;
    }else if(this.isDetail){
      height = 277;
    }
    this.tableHeight = this.getHeight(height);
    if (this.data?.hideFullScreenIcon) {
      this.hideFSIcon = this.data?.hideFullScreenIcon;
    }
    if (this.hideFullScreenIcon) {
      this.hideFSIcon = true;
    }
    this.getData();
    this.updateFeatureDescription();

    this.searchControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((search) => {
      this.highlightSearchInMessages(search);
    });
    this.messages = this.messageInput;

    if (this.type === 'deployment' && this.postingBlueprint) {
      this.getCopilotData();
    }

    this.getSignal();
  }

  trackByFeaturesId(index: number, list: any) {
    return list.Id || list.title;
  }

  trackByMessageId(index: number, list: any) {
    return list.Id;
  }

  toggleFullScreen(state) {
    this.fullScreenToggle.emit(state);
    this.emitMessage.emit(this.messages);
    if (state) {
      const matDialogContainer = this.elRef.nativeElement.closest('mat-dialog-container');
      if (matDialogContainer) {
        matDialogContainer.classList.add('fullScreen-padding');
      }
    } else {
      const matDialogContainer = this.elRef.nativeElement.closest('mat-dialog-container');
      if (matDialogContainer) {
        matDialogContainer.classList.remove('fullScreen-padding');
      }
    }
  }

  updateFeatureDescription() {
    const byType: Record<string, { title: string; description: string }[]> = {
      inventory: [
        { title: '⚡ Summarize',           description: 'Provide a quick summary of this inventory' },
        { title: '🔍 Find Issues',          description: 'Identify misconfigurations or compliance gaps' },
        { title: '📈 Optimize',             description: 'Recommend cost, performance, or security improvements' },
        { title: '🔄 Compare to Blueprint', description: 'Show drift between deployed state and blueprint' }
      ],
      device: [
        { title: '⚡ Summarize',    description: 'Provide a quick summary of this device' },
        { title: '🔍 Inspect Config', description: 'Review the raw device configuration in detail' },
        { title: '🛡️ Security Check', description: 'Check for security misconfigurations or weak policies' },
        { title: '📋 Compliance',    description: 'Verify compliance parameters against the benchmark' }
      ],
      blueprint: [
        { title: '⚡ Overview',          description: 'Explain what this blueprint deploys' },
        { title: '🔍 Validate',          description: 'Check for missing attributes or dependency issues' },
        { title: '📈 Best Practices',    description: 'Suggest improvements for modularity and reuse' },
        { title: '🌐 Generate Terraform', description: 'Generate Terraform code for this blueprint' }
      ],
      job: [
        { title: '⚡ Summarize', description: 'Provide a quick summary of this job' },
        { title: '🔍 Review',    description: 'Review the current job configuration' },
        { title: '📈 Improve',   description: 'Recommend optimizations for this job' }
      ]
    };

    if (this.isGlobal) {
      this.features = [
        { title: '🔍 Find resources',     description: 'Find all VNet blueprints in East US' },
        { title: '⚡ Summarize',           description: 'Summarize my deployed inventories' },
        { title: '🔄 Drift check',         description: 'Which inventories have drifted from their blueprints?' },
        { title: '🛡️ Compliance',          description: 'Which devices are not compliant?' }
      ];
    } else {
      this.features = byType[this.type] ?? [
        { title: '⚡ Summarize', description: 'Provide a quick summary' },
        { title: '🔍 Review',    description: 'Review current configuration' },
        { title: '📈 Improve',   description: 'Recommend optimizations' }
      ];
    }
  }

  get isSendDisabled(): boolean {
    return (
      !(this.formChat.get('message')?.value || this.uploadedFileContent) || this.fetchingAiResponse
    );
  }

  triggerFileUpload(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.txt,.tf,.json,.yaml,.yml';
    input.style.display = 'none';
    input.onchange = (event: Event) => this.handleFileUpload(event);
    document.body.appendChild(input);
    input.click();
    input.addEventListener('change', () => {
      document.body.removeChild(input);
    });
  }

  handleFileUpload(event: Event): void {
    const fileInput = event.target as HTMLInputElement;
    const file = fileInput.files?.[0];
    if (!file) return;
    if (file.size === 0) {
      this.toastrService.info('Upload file can not be empty!');
      return;
    }

    this.fileError = '';
    this.uploadedFileContent = '';
    this.uploadedFileName = '';

    const allowedExtensions = ['txt', 'tf', 'json', 'yaml', 'yml'];
    const fileExtension = file.name.split('.').pop()?.toLowerCase();

    if (!allowedExtensions.includes(fileExtension)) {
      this.fileError = 'Unsupported file type.';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const content = reader.result as string;
      this.uploadedFileContent = content;
      this.uploadedFileName = file.name;
    };

    reader.onerror = () => {
      this.fileError = 'Error reading file.';
    };

    reader.readAsText(file);
  }

  removeUploadedFile(): void {
    this.uploadedFileName = null;
    this.uploadedFileContent = null;
    this.fileError = null;
  }

  getFormattedTimeForFilename(): string {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    return `${mm}${dd}${yyyy}`;
  }

  exportChat(): void {
    const chatContent = this.messages
      .map((message) => {
        const sender = message.from === 'me' ? 'You' : 'Kumobot';
        let exportMessage = '';

        if (sender === 'You' && message.isFileUpload && message.fileDetails) {
          const fileHeader = message.fileDetails;
          const fileContent = this.extractFileContentFromMessage(message.message);

          const hasText = message.textMessage?.trim();
          if (!hasText && !fileContent?.trim()) {
            return null;
          }
          exportMessage = `${hasText || ''}\n\n${fileHeader}\n\n${fileContent}`.trim();
        } else {
          if (!message.message?.trim()) {
            return null;
          }
          exportMessage = message.message;
        }

        return `${message.timestamp} - ${sender}:\n\n${exportMessage}`;
      })
      .join('\n\n---\n\n');

    const blob = new Blob([chatContent], { type: 'text/markdown' });

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = this.isGlobal
      ? `KumoAgent-ChatExport-${this.getFormattedTimeForFilename()}.md`
      : `${this.typeData?.Name ? this.typeData.Name + '-' : ''}${this.typeData?.Version ? 'v' + this.typeData.Version + '-' : ''}KumoAgent-ChatExport-${this.getFormattedTimeForFilename()}.md`;

    this.toastrService.success('Chat exported successfully.');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  extractFileContentFromMessage(message: string): string {
    const lines = message.split('\n');
    const fileLineIndex = lines.findIndex((line) => line.trim().startsWith('Attached File:'));

    if (fileLineIndex !== -1 && fileLineIndex + 1 < lines.length) {
      return lines
        .slice(fileLineIndex + 1)
        .join('\n')
        .trim();
    }
    return '';
  }

  getSignal() {
    this.signal.getDataBySignalR('AiMessageResponse').pipe(takeUntil(this.destroy$)).subscribe((res) => {
      if (res) {
        let message = this.messages.find((x) => x.id == res.MessageId);
        if (!message && this.currentId == res.MessageId) {
          message = this.addPendingAiMessage(res.MessageId);
        }

        if (message && message.id && this.isMatchingAiSignal(res)) {
          if (res.FirstMessage) {
            this.firstMessage = res.FirstMessage;
          }
          if (res.Messages) {
            message.message += res.Messages;
            setTimeout(() => this.scrollToBottom(), 0);
          }
          if (res.Completed) {
            message.isCompleted = true;
          }
          this.completeAiLoading();
        }
      }
    });

    // Agent tool-call events — emitted by the backend when the agentic RAG loop
    // calls an external tool (search, terraform fetch, etc.).
    this.signal.getDataBySignalR('ToolCallStarted').pipe(takeUntil(this.destroy$)).subscribe((res) => {
      if (!res || this.currentId !== res.MessageId) return;
      (res.ToolNames as string[] || []).forEach(name => {
        this.activeSteps.push({ name: this.formatToolName(name), status: 'loading', summary: '' });
      });
      // Ensure the placeholder message exists so steps render immediately.
      if (!this.messages.find(m => m.id === res.MessageId)) {
        this.addPendingAiMessage(res.MessageId);
      } else {
        const msg = this.messages.find(m => m.id === res.MessageId);
        if (msg) msg.steps = this.activeSteps;
      }
      this.changeDetectorRef.detectChanges();
    });

    this.signal.getDataBySignalR('ToolCallCompleted').pipe(takeUntil(this.destroy$)).subscribe((res) => {
      if (!res || this.currentId !== res.MessageId) return;
      (res.Summaries as string[] || []).forEach(summary => {
        const step = this.activeSteps.find(s => s.status === 'loading');
        if (step) { step.status = 'done'; step.summary = summary; }
      });
      this.changeDetectorRef.detectChanges();
    });
  }

  // Maps internal tool names to human-readable labels shown in the chat UI.
  private formatToolName(name: string): string {
    const map: Record<string, string> = {
      search_infrastructure:          '🔍 Searching infrastructure',
      get_terraform_for_inventory:    '📄 Fetching Terraform for inventory',
      get_terraform_for_blueprint:    '📄 Fetching Terraform for blueprint',
      get_device_config:              '📡 Fetching device configuration',
      get_drift_analysis:             '🔄 Analysing drift',
      get_ip_pool:                    '🌐 Fetching IP pool data'
    };
    return map[name] ?? name.replace(/_/g, ' ');
  }

  addPendingAiMessage(messageId: string): any {
    const message = {
      from: 'partner',
      message: '',
      timestamp: this.kumobotService.getFormattedTime(),
      isFileUpload: this.isFileUpload,
      fileDetails: this.showFileUploadDetails,
      isCompleted: false,
      id: messageId,
      steps: this.activeSteps,   // live reference — updates as tool calls arrive
      stepsExpanded: true
    };

    this.messages.push(message);
    return message;
  }

  completeAiLoading() {
    this.serviceConflictCopilotLoading = false;
    this.fetchingAiResponse = false;
    this.stopThinkingAnimation();
    this.changeDetectorRef.detectChanges();
  }

  private startThinkingAnimation() {
    this.thinkingLabelIndex = 0;
    this.thinkingLabel = this.thinkingLabels[0];
    clearInterval(this.thinkingInterval);
    this.thinkingInterval = setInterval(() => {
      this.thinkingLabelIndex = (this.thinkingLabelIndex + 1) % this.thinkingLabels.length;
      this.thinkingLabel = this.thinkingLabels[this.thinkingLabelIndex];
      this.changeDetectorRef.detectChanges();
    }, 1800);
  }

  private stopThinkingAnimation() {
    clearInterval(this.thinkingInterval);
    this.thinkingLabel = 'Thinking';
  }

  isMatchingAiSignal(res: any): boolean {
    if (this.isGlobal || this.type === 'deployment') {
      return this.currentId == res.MessageId;
    }
    if (this.type === 'inventory') {
      return this.chatHistory.InventoryId == res.ReferenceId;
    }
    return this.chatHistory.DeviceId == res.ReferenceId;
  }

  // Opens markdown links in a new tab instead of navigating in the same window.
  onMarkdownClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    const anchor = target.closest('a') as HTMLAnchorElement;
    if (anchor?.href) {
      event.preventDefault();
      window.open(anchor.href, '_blank');
    }
  }

  send() {
    this.fetchingAiResponse = true;
    this.activeSteps = [];
    this.startThinkingAnimation();
    let baseMessage = this.formChat.get('message').value;
    let fileMessage = this.uploadedFileContent
      ? `\n\nAttached File: ${this.uploadedFileName}\n${this.uploadedFileContent}`
      : '';
    this.showFileUploadDetails = this.uploadedFileContent
      ? `\n\nAttached File: ${this.uploadedFileName} ${(
          new Blob([this.uploadedFileContent]).size / 1024
        ).toFixed(2)} KB`
      : '';
    this.showFileUploadDetails ? (this.isFileUpload = true) : (this.isFileUpload = false);

    let fullMessage = baseMessage + fileMessage;
    this.formChat.get('message').setValue('');
    this.chatHistory.Messages = [];
    if (
      this.type === 'deployment'
        ? this.messages && this.messages.length > 0
        : this.messages && this.messages.length > 1
    ) {
      this.messages.forEach((message, index) => {
        if (message.from == 'me') {
          if (index == 0) {
            this.chatHistory.Messages.push({ role: 'user', content: this.firstMessage });
          } else {
            this.chatHistory.Messages.push({ role: 'user', content: message.message });
          }
        } else {
          this.chatHistory.Messages.push({ role: 'assistant', content: message.message });
        }
      });
    }
    this.currentId = uuid();
    this.chatHistory.Messages.push({ role: 'user', content: fullMessage, Id: this.currentId });
    this.messages = [
    ...this.messages,
    {
      from: 'me',
      message: fullMessage,
      timestamp: this.kumobotService.getFormattedTime(),
      isFileUpload: this.isFileUpload,
      fileDetails: this.showFileUploadDetails,
      textMessage: baseMessage
    }
  ];
  this.emitMessage.emit(this.messages);
  this.changeDetectorRef.detectChanges();
    const defaults = this.kumobotService.getDefaultAiSettings(this.type);
    this.chatHistory.AiSetting = {
      Model: this.aiSettingFromDialog?.Model ?? '04-mini',
      KumoBotRole: this.aiSettingFromDialog?.KumoBotRole || defaults.role,
      SpecificInstructions: this.aiSettingFromDialog?.SpecificInstructions || defaults.instructions
    };
    this.uploadedFileContent = '';
    this.uploadedFileName = '';
    this.fileError = '';
    if (!this.isGlobal) {
      if (this.type == 'inventory') {
        this.chatHistory.InventoryId = this.inventoryId;
        delete this.chatHistory?.DeviceId;
      } else {
        this.chatHistory.DeviceId = this.deviceId;
        delete this.chatHistory?.InventoryId;
      }
      this.type == 'deployment'
        ? this.ai.getServiceConflictCoPilot({
            ...this.chatHistory,
            ConflictContext: this.conflictContext
          }).pipe(takeUntil(this.destroy$)).subscribe((res) => {})
        : this.ai.getStreamingResponseByReferenceId(this.chatHistory).pipe(takeUntil(this.destroy$)).subscribe((messages) => {});
    } else {
      // Global KumoAgent: use the agentic streaming endpoint.
      // The agent automatically searches infrastructure and streams back via SignalR.
      this.chatHistory.MessageId = this.currentId;
      this.chatHistory.ReferenceId = this.currentId;
      this.ai.askAgentStream(this.chatHistory).pipe(takeUntil(this.destroy$)).subscribe(() => {});
    }
    setTimeout(() => {
      let chatElement = document.getElementById('chatElement');
      chatElement.scrollTop = chatElement.scrollHeight + 300;
    }, 100);
  }

  hasValidInput(): boolean {
    const message = this.formChat.get('message')?.value || '';
    let lastMessage = this.messages?.find((x) => x.isCompleted == false);
    return (message.trim().length > 0 && !lastMessage) || !!this.uploadedFileContent;
  }

  getHeight(height: number = 0) {
    if (this.isInventoryChild) {
      height += 50;
    } else if (this.isGlobal) {
      height = height - 60;
    } else if (this.isReview) {
      height += 32;
    } else if (this.fullScreen) {
      height -= 161;
    } else if (this.type === 'deployment') {
      height += 80;
    }
    const height_inner = (window.innerHeight - height).toString();
    return height_inner + 'px';
  }

  copyToClipboard(content: any, message: any): void {
    this.kumobotService.copyToClipboard(content, message);
  }

  setFeatures(feature) {
    let inCompleteMessage = this.messages.find((x) => x.isCompleted == false);
    if (!this.fetchingAiResponse && !inCompleteMessage) {
      this.formChat.get('message').setValue(feature.description);
      this.send();
    }
  }

  getData() {
    this.layoutService.userName = this.authService.getUserProperty('Name');
  }

  settings() {
    const defaults = this.kumobotService.getDefaultAiSettings(this.type);
    const dialogData = {
      model: this.aiSettingFromDialog?.Model ?? '',
      role: this.aiSettingFromDialog?.KumoBotRole || defaults.role,
      instructions: this.aiSettingFromDialog?.SpecificInstructions || defaults.instructions,
      toggleMode: this.toggleMode
    };
    const ref = this.dialog.open(KumobotSettings, {
      maxWidth: '100vw',
      maxHeight: '100vh',
      width: '50%',
      data: dialogData
    });

    ref
      .afterClosed()
      .pipe(takeUntil(this.destroy$)).subscribe((result: { isSaved: boolean; data: AiSetting; toggleMode: string }) => {
        if (result?.isSaved) {
          this.aiSettingFromDialog = result.data;
          this.toggleMode = result.toggleMode;
        }
      });
  }

  regenerate() {
    const lastUser = [...this.messages].reverse().find(m => m.from === 'me');
    if (!lastUser || this.fetchingAiResponse) return;
    // Remove the last AI response so it gets regenerated fresh.
    const lastPartnerIdx = [...this.messages].map((m, i) => ({ m, i }))
      .reverse().find(({ m }) => m.from === 'partner')?.i;
    if (lastPartnerIdx !== undefined) this.messages.splice(lastPartnerIdx, 1);
    this.formChat.get('message').setValue(lastUser.textMessage ?? lastUser.message);
    this.send();
  }

  // Template helper — arrow functions are not allowed inside Angular template bindings.
  hasLoadingSteps(steps: any[]): boolean {
    return steps?.some(s => s.status === 'loading') ?? false;
  }

  rateMessage(message: any, rating: 'up' | 'down') {
    if (message.rating === rating) {
      message.rating = null;   // toggle off
    } else {
      message.rating = rating;
    }
    this.changeDetectorRef.detectChanges();
  }

  handleKeydown(event: KeyboardEvent) {
    if (this.fetchingAiResponse) {
      event.preventDefault();
      return;
    }

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (this.hasValidInput()) {
        this.send();
      }
    }
  }

  autoResize(event: Event) {
    const textarea = event.target as HTMLTextAreaElement;
    const maxHeight = 72;

    textarea.style.height = 'auto';
    const newHeight = textarea.scrollHeight;
    textarea.style.height = `${Math.min(newHeight, maxHeight)}px`;
  }

  highlightSearchInMessages(search: string) {
    this.matchedElements = [];
    this.currentMatchIndex = 0;

    if (!search) {
      this.messages = this.messages.map((msg) => ({
        ...msg,
        highlightedMessage: msg.textMessage,
        highlightedMarkdown: msg.message
      }));
      return;
    }

    const escapedSearch = search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escapedSearch})`, 'gi');

    this.messages = this.messages.map((msg) => {
      const newTextMsg =
        msg.textMessage?.replace(regex, '<mark class="search-match">$1</mark>') || '';
      const newMarkdown = msg.message?.replace(regex, '<mark class="search-match">$1</mark>') || '';
      return {
        ...msg,
        highlightedMessage: newTextMsg,
        highlightedMarkdown: newMarkdown
      };
    });

    setTimeout(() => {
      this.matchedElements = Array.from(
        document.querySelectorAll('mark.search-match')
      ) as HTMLElement[];
      this.currentMatchIndex = 0;
      if (this.matchedElements.length) {
        this.scrollToMatch(0);
      }
    }, 0);
  }

  scrollToMatch(index: number) {
    if (!this.matchedElements.length) return;
    this.matchedElements.forEach((el, i) => {
      el.classList.remove('active-match');
      if (i === index) {
        el.classList.add('active-match');
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });

    this.currentMatchIndex = index;
  }

  goToNextMatch() {
    if (!this.matchedElements.length) return;
    const nextIndex = (this.currentMatchIndex + 1) % this.matchedElements.length;
    this.scrollToMatch(nextIndex);
  }

  goToPreviousMatch() {
    if (!this.matchedElements.length) return;
    const prevIndex =
      (this.currentMatchIndex - 1 + this.matchedElements.length) % this.matchedElements.length;
    this.scrollToMatch(prevIndex);
  }

  openPreDeploymentDetail() {
    const dialogRef = this.dialog.open(PreDeploymentDialogueComponent, {
      width: '98%',
      maxWidth: '98vw',
      height: '95%',
      maxHeight: '95vw',
      disableClose: true,
      data: {
        postingBlueprint: this.postingBlueprint,
        conflictContext: this.conflictContext
      }
    });
    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((dialogResult) => {
      if (dialogResult?.disableClose === true) {
        this.nextStepper.emit();
      }
    });
  }

  getCopilotData() {
    this.serviceConflictCopilotLoading = true;
    this.currentId = uuid();
    let transformed = {
      // Beta already validated the full candidate payload. Avoid sending that large
      // graph a second time when the same structured evidence is available.
      Inventories: this.conflictContext ? [] : this.postingBlueprint,
      MessageId: this.currentId,
      ConflictContext: this.conflictContext
    };
    this.ai.getServiceConflictCoPilot(transformed).pipe(takeUntil(this.destroy$)).subscribe(
      (res) => {
        this.completeAiLoading();
      },
      (err) => {
        this.completeAiLoading();
        this.messages.push({
          from: 'partner',
          message: 'Unable to generate the service conflict report. Please open Service Conflict Detail or try again.',
          timestamp: this.kumobotService.getFormattedTime(),
          isCompleted: true,
          id: this.currentId
        });
      }
    );
  }

  scrollToBottom() {
  const chatElement = document.getElementById('chatElement');
  if (chatElement) {
    // smooth scrolling while new chunks arrive
    chatElement.scrollTop = chatElement.scrollHeight;
  }
}
}
